// @flow

import { v4 } from 'uuid';
import { FormattedMessage, FormattedDate } from 'react-intl';
import React, { Fragment } from 'react';
import type { Node } from 'react';
import { useQuery } from '@apollo/client';
import { Section, SectionContent, Wrapper } from '@riseart/layout';
import { GridSquare, GridSquareItem } from '@riseart/grid';
import { CardArticle } from '@riseart/article';
import { Link, LazyloadPlaceholder } from '@riseart/common';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { PicturePlaceholder } from 'shared_components/common/artdirection/picture/Picture';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import FILTER_ARTICLES from 'shared_data/queries/articles/filter.graphql';

type Props = {
  queryVars: Object,
  title: Node,
};

const preloaderId = v4();

export function FurtherReadingSection({ queryVars, title }: Props) {
  const locale = useLocale();
  const LAZYLOAD_ARTICLE_CONFIG = {
    once: true,
    height: 175,
    offset: 200,
    placeholder: <LazyloadPlaceholder height={175} />,
  };
  const { loading, data, error } = useQuery(FILTER_ARTICLES, {
    ssr: false,
    variables: {
      inputArticleFilter: {
        page: 1,
        items: 4,
        ...queryVars,
      },
    },
  });
  const { filterArticles: { items } = {} } = data || {};

  if (error || (!loading && (!items || !items.length))) {
    return null;
  }

  if (loading) {
    return (
      <Fragment>
        <DataProviderPreloaderPage uniqueId={preloaderId} attach={loading} />
        <Section>
          <Wrapper
            sizes={{
              xs: 'wide',
              sm: 'wide',
              md: 'wide',
              lg: 'narrow',
              xl: 'narrow',
            }}
          >
            <SectionContent title={title}>
              <GridSquare columnCount={2} justify="space-between">
                {Array.apply(0, Array(4)).map((item, idx) => (
                  <GridSquareItem key={idx} columnCount={2}>
                    <CardArticle key={idx} displayStyle={3} placeholder />
                  </GridSquareItem>
                ))}
              </GridSquare>
            </SectionContent>
          </Wrapper>
        </Section>
      </Fragment>
    );
  }

  return (
    <Section>
      <Wrapper
        sizes={{
          xs: 'wide',
          sm: 'wide',
          md: 'wide',
          lg: 'narrow',
          xl: 'narrow',
        }}
      >
        <SectionContent title={title}>
          <GridSquare columnCount={2} justify="space-between">
            {items.map(
              ({ id, type, slug, title, summary, publishedDate, images, author, categories }) => {
                const articleUrl = UrlAssembler.articleDetail(id, slug, type, { locale });
                const authorUrl =
                  author && author.type
                    ? UrlAssembler.authorDetail(author.type, author.typeId, author.typeAlias, {
                        locale,
                      })
                    : null;

                return (
                  <GridSquareItem key={id} columnCount={2}>
                    <CardArticle
                      displayStyle={3}
                      title={
                        <Link to={articleUrl} title={title}>
                          {title}
                        </Link>
                      }
                      subtitle={
                        categories && categories[0] ? (
                          <Link
                            to={UrlAssembler.articleCategory(
                              categories[0].uri,
                              categories[0].type,
                              { locale },
                            )}
                            title={categories[0].name}
                          >
                            {categories[0].name}
                          </Link>
                        ) : null
                      }
                      description={summary}
                      date={
                        <FormattedDate
                          value={new Date(publishedDate)}
                          year="numeric"
                          month="short"
                          day="2-digit"
                        >
                          {(dateLabel: string) => dateLabel}
                        </FormattedDate>
                      }
                      author={
                        <FormattedMessage
                          id="common.byName"
                          values={{
                            name: authorUrl ? (
                              <Link to={authorUrl} title={author.fullName}>
                                {author.fullName}
                              </Link>
                            ) : (
                              author.fullName
                            ),
                          }}
                        />
                      }
                    >
                      <Link to={articleUrl} title={title}>
                        <PicturePlaceholder
                          type="blog.gridTwoCol"
                          artDirectionKey="article.main"
                          image={extractImageFileFromData.articleImage(images)}
                          alt={title}
                          lazyload={LAZYLOAD_ARTICLE_CONFIG}
                        />
                      </Link>
                    </CardArticle>
                  </GridSquareItem>
                );
              },
            )}
          </GridSquare>
        </SectionContent>
      </Wrapper>
    </Section>
  );
}
